import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BellRing, ArrowUpRight } from "lucide-react";
import { EVENT, LOGO_URL } from "@/config";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pad = (n) => String(n).padStart(2, "0");

const getParts = () => {
  const diff = Math.max(0, new Date(EVENT.startIso).getTime() - Date.now());
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor(diff / 3600000) % 24,
    minutes: Math.floor(diff / 60000) % 60,
    seconds: Math.floor(diff / 1000) % 60,
  };
};

export const CountdownTimer = () => {
  const [t, setT] = useState(getParts);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setT(getParts()), 1000);
    return () => clearInterval(id);
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/reminders`, { email });
      if (res.data.message === "already_subscribed") {
        toast.success("You're already on the reminder list — see you race day!");
      } else {
        toast.success("You're on the list! Watch your inbox for a confirmation.");
      }
      setEmail("");
    } catch {
      toast.error("Couldn't add you — please check the email and try again.");
    } finally {
      setSending(false);
    }
  };

  const units = [
    { label: "Days", value: t.days, testid: "countdown-days" },
    { label: "Hours", value: pad(t.hours), testid: "countdown-hours" },
    { label: "Minutes", value: pad(t.minutes), testid: "countdown-minutes" },
    { label: "Seconds", value: pad(t.seconds), testid: "countdown-seconds" },
  ];

  return (
    <section
      data-testid="countdown-section"
      className="relative overflow-hidden border-b border-black/5 bg-white py-16 md:py-20"
    >
      <div className="mx-auto max-w-7xl px-4 text-center md:px-8">
        <Reveal>
          <img
            src={LOGO_URL}
            alt="2Run4Hope logo"
            data-testid="countdown-logo"
            className="mx-auto mb-8 h-24 w-24 rounded-full shadow-md ring-4 ring-hope-green/15 md:h-28 md:w-28"
          />
          <p
            data-testid="countdown-overline"
            className="mb-8 flex items-center justify-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-hope-red md:text-sm"
          >
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-hope-red" />
            The Starting Gun Fires In
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="flex items-start justify-center gap-3 sm:gap-6 md:gap-10" data-testid="countdown-units">
            {units.map((u, i) => (
              <div key={u.label} className="flex items-start gap-3 sm:gap-6 md:gap-10">
                {i > 0 && (
                  <span className="mt-2 font-heading text-4xl font-black text-hope-red/60 sm:mt-3 md:text-6xl">
                    :
                  </span>
                )}
                <div className="min-w-[72px] sm:min-w-[110px]">
                  <p
                    data-testid={u.testid}
                    className="font-heading text-5xl font-black tabular-nums tracking-tighter text-hope-ink sm:text-7xl md:text-8xl"
                  >
                    {u.value}
                  </p>
                  <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-500 sm:text-xs">
                    {u.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <p data-testid="countdown-meta" className="mt-8 text-sm text-zinc-500 md:text-base">
            {EVENT.date} · Festivities 7:00 AM · 5K Gun 8:00 AM · {EVENT.venue}, Allen TX
          </p>
        </Reveal>

        <Reveal delay={0.3}>
          <div
            data-testid="reminder-signup"
            className="mx-auto mt-10 flex max-w-xl flex-col items-center gap-4 rounded-3xl border border-hope-green/20 bg-hope-cream p-6 sm:p-7"
          >
            <p className="flex items-center gap-2 font-heading text-lg font-bold text-hope-ink">
              <BellRing className="h-5 w-5 text-hope-green" />
              Get race-day reminders
            </p>
            <p className="-mt-2 text-sm text-zinc-500">
              We'll email you one week out, the day before, and race morning.
            </p>
            <form onSubmit={subscribe} className="flex w-full flex-col gap-3 sm:flex-row" data-testid="reminder-form">
              <input
                data-testid="reminder-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="flex-1 rounded-full border border-black/10 bg-white px-5 py-3.5 text-sm text-hope-ink placeholder-zinc-400 outline-none transition-colors duration-200 focus:border-hope-green"
              />
              <button
                data-testid="reminder-submit-btn"
                type="submit"
                disabled={sending}
                className="group inline-flex items-center justify-center gap-1.5 rounded-full bg-hope-green px-6 py-3.5 font-heading text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-hope-ink disabled:opacity-50"
              >
                {sending ? "Adding…" : "Remind Me"}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
